import CommonSwal from "../components/Swal/commonSwal";

/**
 * Atualiza o estado de um formulário React quando um evento de mudança ocorre.
 *
 * @param {Event} e - O evento de mudança.
 * @param {Object} data - O estado atual do formulário.
 * @param {Function} setData - A função para atualizar o estado do formulário.
 * @param {string|null} [name=null] - O nome do campo a ser atualizado. Se não fornecido, o nome será extraído do evento.
 */
export function handleChange(e, data, setData, name = null) {
  setData({ ...data, [name ?? e.target.name]: e.target.value });
}

/**
 * Atualiza o estado de um formulário React quando um evento de mudança de checkbox ocorre.
 *
 * @param {Event} e - O evento de mudança de checkbox.
 * @param {Object} data - O estado atual do formulário.
 * @param {Function} setData - A função para atualizar o estado do formulário.
 */
export function handleChangeCheck(e, data, setData) {
  const inputValue = e.target.checked;

  setData({ ...data, [e.target.name]: inputValue });
}

/**
 * Atualiza o estado de um formulário React quando um evento de mudança ocorre mantendo apenas números.
 *
 * @param {Event} e - O evento de mudança numérica.
 * @param {Object} data - O estado atual do formulário.
 * @param {Function} setData - A função para atualizar o estado do formulário.
 * @param {number|null} [min=null] - O valor mínimo permitido.
 * @param {number|null} [max=null] - O valor máximo permitido.
 */
export function handleChangeNumeral(e, data, setData, min = null, max = null) {
  const inputValue = e.target.value.replace(/[^0-9]/g, "");
  var intValue = inputValue === "" ? "" : parseInt(inputValue);

  if (min && intValue < min && intValue !== "") intValue = min;

  if (max && intValue > max && intValue !== "") intValue = max;

  setData({ ...data, [e.target.name]: intValue });
}

/**
 * Atualiza o estado de um formulário React quando um evento de mudança de arquivo ocorre.
 *
 * @param {Event} e - O evento de mudança de arquivo.
 * @param {Object} data - O estado atual do formulário.
 * @param {Function} setData - A função para atualizar o estado do formulário.
 */
export function handleChangeFile(e, data, setData) {
  setData({
    ...data,
    [e.target.name]: e.target.files ? e.target.files[0] : "",
  });
}

/**
 * Atualiza o estado de um formulário React com um par nome-valor específico.
 *
 * @param {string} name - O nome do campo a ser atualizado.
 * @param {any} value - O valor a ser atribuído ao campo.
 * @param {Object} data - O estado atual do formulário.
 * @param {Function} setData - A função para atualizar o estado do formulário.
 */
export function handleChangeNameValue(name, value, data, setData) {
  setData({ ...data, [name]: value });
}

/**
 * Atualiza o estado de checkboxes em uma tabela React quando um evento de mudança ocorre.
 *
 * @param {Event} e - O evento de mudança.
 * @param {Array} prevCheckboxes - O array de checkboxes anteriores.
 * @param {Function} setCheckboxes - A função para atualizar o array de checkboxes.
 */
export function handleChangeTableCheckbox(e, prevCheckboxes, setCheckboxes) {
  const checkboxValue = e.target.value;

  if (e.target.checked) {
    setCheckboxes([...prevCheckboxes, checkboxValue]);
  } else {
    setCheckboxes(prevCheckboxes.filter((value) => value !== checkboxValue));
  }
}

/**
 * Atualiza o estado de um formulário React quando um evento de mudança de imagem ocorre.
 *
 * @param {Event} e - O evento de mudança de imagem.
 * @param {Object} data - O estado atual do formulário.
 * @param {Function} setData - A função para atualizar o estado do formulário.
 */
export function handleChangeImage(e, data, setData) {
  const file = e.target.files ? e.target.files[0] : "";
  const allowedExtensions = /(\.jpg|\.png|\.jpeg)$/i;

  if (!allowedExtensions.exec(file && file.name)) {
    setData({
      ...data,
      [e.target.name]: "",
      imagePreview: "",
    });
    CommonSwal({
      title: "Por favor, selecione uma imagem.",
    });
    return;
  }

  setData({
    ...data,
    [e.target.name]: e.target.files ? e.target.files[0] : "",
    imagePreview: e.target.files ? URL.createObjectURL(e.target.files[0]) : "",
  });
}

/**
 * Atualiza o estado de um formulário React removendo caracteres especiais de um campo específico.
 *
 * @param {Event} e - O evento de mudança.
 * @param {Object} data - O estado atual do formulário.
 * @param {Function} setData - A função para atualizar o estado do formulário.
 * @param {string|null} [name=null] - O nome do campo a ser atualizado. Se não fornecido, o nome será extraído do evento.
 */
export function handleChangeUnmask(e, data, setData, name = null) {
  setData({
    ...data,
    [name ?? e.target.name]: e.target.value.replace(/[^a-zA-Z0-9]/g, ""),
  });
}

/**
 * Marca ou desmarca todas as caixas de seleção em um conjunto de linhas.
 *
 * @param {Object} checkboxesHook - O estado do gancho das caixas de seleção.
 * @param {Object} linhas - O conjunto de linhas que contém as caixas de seleção.
 * @param {string} [accessor="uid"] - O acessor para obter o valor único de cada linha.
 */
export function handleCheckAll(checkboxesHook, linhas, accessor = "uid") {
  const { checkboxesMarcados, setCheckboxesMarcados } = checkboxesHook;
  if (checkboxesMarcados.length < linhas.length) {
    const novosCheckboxesMarcados = linhas.map((linha) =>
      String(linha[accessor])
    );
    setCheckboxesMarcados(novosCheckboxesMarcados);
  } else setCheckboxesMarcados([]);
}

/**
 * Atualiza os dados com novos valores de seleção para um conjunto de caixas de seleção.
 *
 * @param {Object} data - O estado atual dos dados que contém as seleções.
 * @param {Function} setData - A função para atualizar o estado dos dados.
 * @param {string} name - O nome da propriedade a ser atualizada nos dados.
 * @param {Array} newData - O novo conjunto de dados que contém as novas seleções.
 * @param {string} value - O nome da propriedade em cada item do novo conjunto de dados que contém o valor de seleção.
 */
export function handleChangeMultipleSelect(
  data,
  setData,
  name,
  newData,
  value
) {
  const newValue = newData.map((item) => item[value]);
  setData({ ...data, [name]: newValue });
}
